import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ArtworkAbout
 *
 * The "About this work" + "About the artist" + gallery footer column on
 * `/artwork/:id`. Extracted from `ArtworkDetailPage.jsx` to keep the page
 * under 600 lines.
 *
 * Props:
 *   - aboutTheWork: string — already-resolved descriptive text
 *   - artist: artist doc (or null)
 *   - formatIntoParagraphs: the page's existing paragraph helper passed in
 *     to keep this component pure / framework-agnostic.
 */
const ArtworkAbout = ({ aboutTheWork, artist, formatIntoParagraphs }) => {
  const artistBio = artist && artist.biography
    ? (artist.biography.length > 600
        ? `${artist.biography.substring(0, 600)}…`
        : artist.biography)
    : null;

  return (
    <>
      {/* About the Work */}
      <div className="pt-6 border-t border-neutral-200 space-y-4">
        <h2 className="text-lg font-medium">About This Work</h2>
        <div
          className="text-sm leading-[1.75] text-neutral-700 space-y-4"
          data-testid="artwork-about-body"
        >
          {formatIntoParagraphs(aboutTheWork).map((para, i) => (
            <p key={`work-${i}-${para.slice(0, 24)}`}>{para}</p>
          ))}
        </div>
      </div>

      {/* About the Artist */}
      {artistBio && (
        <div className="pt-6 border-t border-neutral-200 space-y-4">
          <h2 className="text-lg font-medium">About the Artist</h2>
          <div
            className="text-sm leading-[1.75] text-neutral-700 space-y-4"
            data-testid="artwork-about-artist"
          >
            {formatIntoParagraphs(artistBio).map((para, i) => (
              <p key={`bio-${i}-${para.slice(0, 24)}`}>{para}</p>
            ))}
          </div>
          {artist.id && (
            <Link
              to={`/artists/${artist.slug || artist.id}`}
              className="inline-flex items-center gap-2 text-sm text-black hover:underline underline-offset-4 font-medium"
              data-testid="artwork-artist-profile-link"
            >
              View Full Artist Profile →
            </Link>
          )}
        </div>
      )}

      {/* Gallery Info */}
      <div className="pt-6 border-t border-neutral-200 space-y-3">
        <h3 className="text-sm font-medium">Zebra One Gallery</h3>
        <p className="text-sm text-neutral-700">
          Contemporary Art • Hampstead, London
        </p>
        <p className="text-xs text-neutral-500">
          Established 1976 • 50 years of excellence in contemporary art • Professional authentication and provenance research
        </p>
      </div>
    </>
  );
};

export default ArtworkAbout;
